import { Listing } from "@t/Listing"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { gsap } from "gsap/dist/gsap"
import css from "@css/modules/BidSuggestion.module.scss"
import { ArrowDown } from "@css/icons/SVG"
import { OfferForecast } from "@t/OfferForecast"
import { CurrentListingPrice } from "./CurrentListingPrice"

export function calculatePercentage(min: number, max: number, number: number) {
    return ((number - min) / (max - min)) * 100
}

export function getNumberFromPercentage(min: number, max: number, percent: number) {
    return min + (percent / 100) * (max - min)
}

type Props = { listing: Listing; setPrice: (price: number) => void; data: OfferForecast }

export function GradientBar({ listing, setPrice: setPriceTop, data }: Props) {
    const [price, setPrice] = useState(0)
    const offerForeCast = useMemo(() => {
        const res = data
        if (res) {
            return {
                ...res,
                star_6: Math.min(res?.star_5 - (res.star_4 - res.star_5), listing.price_per_point)
            }
        }
        return null
    }, [data, listing.price_per_point])
    const gradientBar = useRef<HTMLDivElement>(null)
    const percentRef = useRef(0)
    const dragRef = useRef({
        isDrag: false
    })
    const { forecastToPercent, suggestedOffer, suggestedPrice } = useMemo(() => {
        let forecastToPercent = 0
        let suggestedOffer = 0
        if (offerForeCast) {
            const star = listing.stars
            const min = offerForeCast["star_" + star]
            const max = offerForeCast["star_" + (star + 1)]
            const percentOfCurrentStar = min === max ? 100 : calculatePercentage(min, max, listing.price_per_point)
            const percentOfWholeLine = percentOfCurrentStar * 0.25
            const fillStarsBefore = (star - 2) * 25

            forecastToPercent = fillStarsBefore + percentOfWholeLine

            suggestedOffer = 100 - (100 - forecastToPercent) / 1.45
        }
        const star = suggestedOffer / 25 + 2
        const floorStar = Math.floor(star)
        const min = offerForeCast["star_" + floorStar]
        const max = offerForeCast["star_" + (floorStar + 1)]
        const percent = floorStar === star ? 0 : Number("0." + star.toString().split(".")[1]) * 100

        const suggestedPrice = getNumberFromPercentage(min, max, percent)
        return { forecastToPercent, suggestedOffer, suggestedPrice }
    }, [listing.price_per_point, offerForeCast])
    useEffect(() => {
        setPriceTop(Math.round(suggestedPrice))
    }, [suggestedPrice, setPriceTop])
    const updatePrice = useCallback(() => {
        if (offerForeCast) {
            setPrice(() => {
                const star = percentRef.current / 25 + 2
                let price = offerForeCast.hasOwnProperty("star_" + star) ? offerForeCast["star_" + star] : 0
                if (!price && offerForeCast["star_1"]) {
                    const floorStar = Math.floor(star)
                    const min = offerForeCast["star_" + floorStar]
                    const max = offerForeCast["star_" + (floorStar + 1)]
                    const percent = floorStar === star ? 0 : Number("0." + star.toString().split(".")[1]) * 100
                    price = getNumberFromPercentage(min, max, percent)
                }
                return Math.round(price)
            })
        }
    }, [offerForeCast])
    useEffect(() => {
        if (!dragRef.current.isDrag) {
            gsap.timeline()
                .to(gradientBar.current, {
                    "--left": suggestedOffer + "%",
                    duration: 0.6
                })
                .to(gradientBar.current, { "--scale": 1.5, ease: "easeOut", duration: 0.2 })
                .to(gradientBar.current, { "--scale": 1, ease: "easeOut", duration: 0.2 })
        }
        percentRef.current = suggestedOffer
        updatePrice()
    }, [forecastToPercent, suggestedOffer, listing, updatePrice])

    const onTouchStart = () => {
        gradientBar.current.style.cursor = "grabbing"
        dragRef.current.isDrag = true
    }
    const setLeft = useCallback(
        function setLeft(x: number) {
            const rect = gradientBar.current!.getBoundingClientRect()
            const width = rect.width
            const left = Math.min(Math.max(0, x - rect.left), width)
            let percent: number
            if (left < 0) {
                percent = 0
            } else if (left > width) {
                percent = 100
            } else {
                percent = (left / width) * 100
            }
            if (percent < suggestedOffer + 2 && percent > suggestedOffer - 2) {
                percent = suggestedOffer
            }
            gradientBar.current.style.setProperty("--left", `${percent}%`)
            gradientBar.current.style.cursor = "grabbing"
            percentRef.current = percent
            updatePrice()
        },
        [updatePrice, suggestedOffer]
    )
    useEffect(() => {
        const upListener = () => {
            gradientBar.current.style.cursor = ""
            dragRef.current.isDrag = false
        }
        const listener = (e: TouchEvent | MouseEvent) => {
            if (!dragRef.current.isDrag) return
            let x
            if ("touches" in e) {
                x = e.touches?.[0]?.clientX
            } else {
                x = e.clientX
            }
            setLeft(x)
        }
        const preventDrag = (e: DragEvent) => {
            const target = e.target as HTMLElement
            if (target?.closest?.("." + css.interactiveContent)) {
                e.preventDefault()
                return false
            }
        }
        document.addEventListener("drag", preventDrag)
        document.addEventListener("dragstart", preventDrag)
        document.addEventListener("mousemove", listener)
        document.addEventListener("touchmove", listener)
        document.addEventListener("mouseup", upListener)
        document.addEventListener("touchend", upListener)
        const onResize = () => {
            gradientBar.current.style.setProperty("--left", percentRef.current + "%")
        }
        window.addEventListener("resize", onResize)
        return () => {
            window.removeEventListener("resize", onResize)
            document.removeEventListener("mousemove", listener)
            document.removeEventListener("touchmove", listener)
            document.removeEventListener("mouseup", upListener)
            document.removeEventListener("touchend", upListener)
            document.removeEventListener("drag", preventDrag)
            document.removeEventListener("dragstart", preventDrag)
        }
    }, [setLeft])
    const onClick = (e) => {
        e.stopPropagation()
        const x = e.clientX
        setLeft(x)
    }
    const onCursorClick = (e) => {
        e.stopPropagation()
    }
    return (
        <div className={css.gradientHeightWrap}>
            <div className={css.gradientWrap} ref={gradientBar} onClick={onClick}>
                <div className={css.gradientBar} />
                <div className={css.cursor} onClick={onCursorClick} onTouchStart={onTouchStart} onMouseDown={onTouchStart}></div>
                <div className={css.cursorInfo}>
                    {price === Math.round(suggestedPrice) ? "Suggested " : ""} Offer: ${price}pp
                </div>
                <CurrentListingPrice suggestedOffer={suggestedOffer} forecastToPercent={forecastToPercent} />
                <div className={css.suggestedOffer} style={{ "--suggestedOffer": suggestedOffer + "%" }} />
                <div className={css.currentListingPrice} style={{ "--left": forecastToPercent + "%" }}>
                    <div className={css.currentListingPP}>${listing.price_per_point}</div>
                    Asking Price
                </div>
                <div className={css.currentListingPriceArrow} style={{ "--left": forecastToPercent + "%" }}>
                    <ArrowDown width={20} />
                </div>
            </div>
        </div>
    )
}
